import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Slider } from './ui';

const StyledImageGallery = styled.section`
  ${sectionMargins()};
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.small`
    padding-right: 0;
    padding-left: 0;
  `}
`;

const StyledSlider = styled(Slider)`
  ${minBreakpointQuery.smedium`
    padding: 0 30px;
  `}

  .swiper-container {
    width: 100%;
  }

  .swiper-pagination {
    margin: 0 auto;
    background: ${brandColours.primary};
    position: absolute;
    max-width: fit-content;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 10px;
    display: flex;

    ${minBreakpointQuery.small`
      padding: 12px 15px;
    `}

    ${minBreakpointQuery.large`
      padding: 16px 20px;
    `}
  }

  .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    background-color: ${standardColours.white};
    opacity: 1;
    transition: ${standardTransition('background-color')};

    ${minBreakpointQuery.small`
      width: 12px;
      height: 12px;
    `}

    ${minBreakpointQuery.large`
      width: 14px;
      height: 14px;
    `};
  }

  .swiper-pagination-bullet-active {
    background-color: ${brandColours.secondary};
  }

  .swiper-slide {
    text-align: center;
    width: auto;
  }

  .swiper-wrapper {
    flex-direction: row !important;
  }

  .swiper-button-prev,
  .swiper-button-next {
    position: absolute;
    background-color: ${standardColours.white};
    height: 100%;
    width: 30px;
    margin-top: -22px;
    top: 22px;
    z-index: 1;

    &:after {
      display: none;
    }

    &:before {
      content: '';
      position: absolute;
      display: block;
      height: 12px;
      width: 12px;
      border-radius: 1px;
      top: calc(50% - var(--progressbar-padding) / 2);
      border: 3px solid ${brandColours.primary};
      border-top: none;
      border-left: none;
      z-index: 1;

      ${minBreakpointQuery.mlarge`
        height: 16px;
        width: 16px;
      `}

      ${minBreakpointQuery.xlarge`
        height: 20px;
        width: 20px;
      `}
    }

    ${maxBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}
  }

  .swiper-button-prev {
    left: 0;
    &:before {
      transform: translateY(-50%) rotate(135deg);
      transition: ${standardTransition('border-color')};
      left: 3px;
    }
  }

  .swiper-button-next {
    right: 0;
    &:before {
      transform: translateY(-50%) rotate(-45deg);
      transition: ${standardTransition('border-color')};
      right: 3px;
    }
  }
`;

const ImageGallery = ({ items }) => {
  const sliderOptions = {
    modules: [Autoplay, Pagination, Navigation],
    slidesPerView: 1,
    loop: true,
    pagination: { clickable: true },
    navigation: true,
    autoplay: {
      delay: 5000,
      pauseOnMouseEnter: true,
      disableOnInteraction: false,
    },
    speed: 1000,
  };

  return (
    <StyledImageGallery>
      <StyledContainer>
        <StyledSlider {...sliderOptions}>
          {items.map(({ gatsbyImageData, alt }, i) => (
            <GatsbyImage
              key={`slider-image-${i}`}
              image={gatsbyImageData}
              alt={alt}
            />
          ))}
        </StyledSlider>
      </StyledContainer>
    </StyledImageGallery>
  );
};

export default ImageGallery;
